import { useEffect, useRef, useSyncExternalStore } from 'react';
import { WorkspaceDialog } from './WorkspaceDialog';
import './confirm-action.css';

/**
 * The app's own yes/no confirmation, in place of window.confirm (which Chromium
 * draws as an unthemed Windows box). Any code can `await confirmAction(...)`;
 * <ConfirmActionHost />, mounted once per window in main.tsx, draws it with
 * WorkspaceDialog so it follows the light/dark theme, traps focus and closes on
 * Escape like every other dialog. Guarded by tests/native-dialog-ui-tests.
 */
export type ConfirmActionRequest = Readonly<{
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  /** Draws the confirm button as a danger button and starts focus on Cancel. */
  destructive?: boolean;
}>;

type Pending = ConfirmActionRequest & { resolve: (confirmed: boolean) => void };

let pending: Pending | null = null;
const listeners = new Set<() => void>();
const publish = (next: Pending | null) => {
  pending = next;
  for (const listener of listeners) listener();
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const current = () => pending;

/** Resolves true only when the operator chooses the confirm button. */
export function confirmAction(request: ConfirmActionRequest): Promise<boolean> {
  // One question at a time: a newer one answers the older "no".
  pending?.resolve(false);
  return new Promise<boolean>((resolve) => {
    const entry: Pending = {
      ...request,
      resolve: (confirmed) => {
        if (pending === entry) publish(null);
        resolve(confirmed);
      },
    };
    publish(entry);
  });
}

export function ConfirmActionHost() {
  const request = useSyncExternalStore(subscribe, current);
  // A window that goes away (HMR, unmount) must not leave a caller waiting forever.
  useEffect(
    () => () => {
      pending?.resolve(false);
    },
    [],
  );
  if (!request) return null;
  return <ConfirmActionDialog key={`${request.title}\n${request.message}`} request={request} />;
}

function ConfirmActionDialog({ request }: { request: Pending }) {
  const actions = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // WorkspaceDialog focuses itself first; then land on the safe choice for a
    // destructive action, or on the action itself, so Enter does what it says.
    const frame = window.requestAnimationFrame(() =>
      actions.current
        ?.querySelector<HTMLButtonElement>(
          request.destructive ? '.secondary-button' : '.primary-button',
        )
        ?.focus(),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [request]);
  return (
    <WorkspaceDialog
      telemetry="confirm-action"
      title={request.title}
      onClose={() => request.resolve(false)}
      compact
    >
      <div className="confirm-action-content">
        <p>{request.message}</p>
        <div ref={actions} className="confirm-action-actions">
          <button type="button" className="secondary-button" onClick={() => request.resolve(false)}>
            {request.cancelLabel ?? 'Cancel'}
          </button>
          {/* confirm-action-confirm: telemetry counts this click as finishing the window (dialogClickCompletes). */}
          <button
            type="button"
            className={`${request.destructive ? 'danger-button' : 'primary-button'} confirm-action-confirm`}
            onClick={() => request.resolve(true)}
          >
            {request.confirmLabel}
          </button>
        </div>
      </div>
    </WorkspaceDialog>
  );
}
